import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Sparkles, ChevronDown } from "lucide-react";
import { useForm, type Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { emailSchema, trimmedStringSchema } from "@/lib/schemas";

// ─── Schema ────────────────────────────────────────────────
// One big schema covers every field across the 6 form steps. Per-step
// validation is handled by calling form.trigger(stepFields[step]) before
// advancing, rhf only surfaces errors for the fields named, so users
// don't see later steps light up red while they're still on an earlier
// step.

const applicationSchema = z.object({
  // Step 0
  bakery_name: trimmedStringSchema({ min: 1, max: 200, label: "your business name" }),
  contact_name: trimmedStringSchema({ min: 1, max: 100, label: "your name" }),
  contact_email: emailSchema,
  contact_phone: z.string().trim().max(30),
  abn: z
    .string()
    .trim()
    .refine(
      (v) => v.replace(/\s/g, "").length >= 11,
      "ABNs are 11 digits. Include all of them.",
    ),
  years_in_business: z.string().min(1, "Pick how long you've been baking."),
  website: z.string().trim().max(200),
  instagram: z.string().trim().max(100),
  // Step 1
  product_types: z
    .array(z.string())
    .min(1, "Pick at least one thing you make."),
  dietary_options: z
    .array(z.string())
    .min(1, "Pick at least one dietary option."),
  // Step 2
  customisation_level: z.string().min(1, "Tell us how customisable your offering is."),
  signature_style: z
    .string()
    .trim()
    .min(50, "Give us at least 50 characters about your signature style."),
  price_range: z.string().min(1, "Pick a typical price range."),
  // Step 3
  delivery_type: z.string().min(1, "Pick how you handle delivery."),
  delivery_suburbs: trimmedStringSchema({ min: 1, max: 1000, label: "your delivery area" }),
  min_notice_days: z.number(),
  max_orders_per_week: z.string().min(1, "Pick your weekly capacity."),
  // Step 4
  team_sizes_served: z
    .array(z.string())
    .min(1, "Pick at least one team size you can serve."),
  handles_urgent_orders: z.string().min(1, "Tell us about urgent-order capability."),
  existing_corporate_clients: z.string().trim().max(500),
  // Step 5
  why_lembra: z
    .string()
    .trim()
    .min(150, "Give us at least 150 characters on why you want to partner."),
  referral_source: z.string().min(1, "Let us know how you heard about us."),
  additional_notes: z.string().trim().max(1000),
});

type AppData = z.output<typeof applicationSchema>;
type AppDataInput = z.input<typeof applicationSchema>;

const EMPTY: AppData = {
  bakery_name: "", contact_name: "", contact_email: "", contact_phone: "",
  abn: "", years_in_business: "", website: "", instagram: "",
  product_types: [], dietary_options: [],
  customisation_level: "", signature_style: "", price_range: "",
  delivery_type: "", delivery_suburbs: "", min_notice_days: 2, max_orders_per_week: "",
  team_sizes_served: [], handles_urgent_orders: "", existing_corporate_clients: "",
  why_lembra: "", referral_source: "", additional_notes: "",
};

// Field lists per step for form.trigger(). Keep these in sync with the
// steps rendered below.
const STEP_FIELDS: Record<number, (keyof AppData)[]> = {
  0: ["bakery_name", "contact_name", "contact_email", "abn", "years_in_business"],
  1: ["product_types", "dietary_options"],
  2: ["customisation_level", "signature_style", "price_range"],
  3: ["delivery_type", "delivery_suburbs", "max_orders_per_week"],
  4: ["team_sizes_served", "handles_urgent_orders"],
  5: ["why_lembra", "referral_source"],
};

// ─── Presentational helpers ──────────────────────────────────
const toggle = (arr: string[], val: string) =>
  arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

const CheckChip = ({
  label, checked, onToggle,
}: { label: string; checked: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium border-2 transition-all ${
      checked ? "border-coral bg-coral/10 text-navy" : "border-charcoal/15 bg-white text-charcoal/70 hover:border-coral/40"
    }`}
  >
    {checked && <Check className="h-3.5 w-3.5 text-coral" />}
    {label}
  </button>
);

const OptionCard = ({
  selected, onClick, title, desc,
}: { selected: boolean; onClick: () => void; title: string; desc?: string }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-2xl p-5 border-2 transition-all ${
      selected ? "border-coral bg-white shadow-md" : "border-transparent bg-white shadow-sm hover:border-coral/30 hover:shadow-md"
    }`}
  >
    <p className="font-bold text-navy">{title}</p>
    {desc && <p className="text-sm text-charcoal/60 mt-0.5">{desc}</p>}
  </button>
);

// ─── Summary row ─────────────────────────────────────────────
const SummaryRow = ({ label, value }: { label: string; value: string | string[] | number }) => {
  const display = Array.isArray(value) ? (value.length ? value.join(", ") : "—") : String(value || "—");
  return (
    <div className="flex gap-3 py-2.5 border-b border-charcoal/8 last:border-0">
      <span className="text-sm text-charcoal/50 w-40 shrink-0">{label}</span>
      <span className="text-sm text-navy font-medium flex-1">{display}</span>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────
export default function PartnerApply() {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<AppDataInput, unknown, AppData>({
    resolver: zodResolver(applicationSchema),
    defaultValues: EMPTY as unknown as AppDataInput,
    mode: "onTouched",
  });

  // Pull the fields we read in the render tree out of form state. Each
  // `watch` subscribes this component to changes of the given field, so
  // checkbox groups and option cards re-render on selection.
  const data = form.watch();
  const submitting = form.formState.isSubmitting;
  const stepErrors = collectStepErrors(form.formState.errors, STEP_FIELDS[step] ?? []);

  const goNext = (next: number) => {
    setTransitioning(true);
    setTimeout(() => { setStep(next); setTransitioning(false); }, 200);
  };
  const goBack = () => {
    if (step === 0) return;
    setTransitioning(true);
    setTimeout(() => { setStep(step - 1); setTransitioning(false); }, 200);
  };

  const handleNext = async () => {
    const fields = STEP_FIELDS[step] ?? [];
    const ok = await form.trigger(fields as Path<AppData>[]);
    if (!ok) return;
    goNext(step + 1);
  };

  const onSubmit = async (values: AppData) => {
    try {
      const { error } = await supabase.from("bakery_applications").insert({
        ...values,
        status: "pending",
      });
      if (error) throw error;
      setSubmitted(true);
    } catch {
      toast({ title: "Submission failed", description: "Please try again later.", variant: "destructive" });
    }
  };

  const TOTAL_STEPS = 7;

  // MS229: expanded beyond the cake-only origins to cover the full
  // multi-vendor catalogue (hampers, florists, spas, experiences,
  // meal kits). The form's free-text "anything else you make" field
  // still catches niche offerings, but the visible chips now match
  // what Lembra actually surfaces to customers.
  //
  // MS309: coffee added (matches the MS278 first-class category +
  // Patricia/Proud Mary's/ST. ALi onboarding). Without these options
  // a coffee-vendor applicant would have to free-text it, signaling
  // we don't take them seriously. Keep the existing 'Coffee Vouchers'
  // language consistent with the chat-concierge wording for the
  // category. Also expanded the wellness section since Inner Studio
  // (MS281) doesn't fit "spa" cleanly — wellness covers studio
  // classes, sound bath, breathwork, etc.
  const productOptions = [
    // Cakes & sweets
    "Birthday Cakes", "Cupcakes", "Macarons", "Tarts & Pastries",
    "Brownies & Slices", "Celebration Boxes", "Grazing Boxes", "Show Cakes",
    // Florists
    "Bouquets", "Flower Arrangements",
    // Hampers
    "Gift Hampers", "Curated Gift Boxes",
    // Spa / wellness
    "Spa Vouchers", "Day Spa Experiences", "Wellness Studio Vouchers",
    // Workshops / experiences
    "Workshop Vouchers", "Experience Vouchers",
    // Coffee (MS278 first-class category)
    "Coffee Vouchers", "Coffee Subscriptions", "Coffee Beans",
    // Food (catering / meal kits)
    "Catering Boxes", "Meal Kits",
  ];
  const dietaryOpts = ["Gluten-Free", "Vegan", "Nut-Free", "Dairy-Free", "Egg-Free", "Halal"];
  const teamSizeOpts = [
    { value: "2_10", label: "2–10 people (small team)" },
    { value: "10_25", label: "10–25 people (medium team)" },
    { value: "25_50", label: "25–50 people (larger office)" },
    { value: "50_plus", label: "50+ people (big events)" },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 py-16"
        style={{ background: "linear-gradient(165deg, #FFF8E7 0%, #FBF3D0 40%, #F7E8A4 70%, #FBF3D0 100%)" }}>
        <div className="max-w-md w-full text-center">
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-butter mb-6">
            <Sparkles className="h-10 w-10 text-navy" />
          </div>
          <h2 className="text-3xl font-bold text-navy mb-3">Application submitted!</h2>
          <p className="text-charcoal/70 mb-2">
            We review all applications within <strong>3 business days</strong>. We'll be in touch at:
          </p>
          <p className="font-bold text-navy mb-8">{data.contact_email}</p>
          <p className="text-sm text-charcoal/50 mb-8">
            In the meantime, keep doing what you do best: baking something wonderful.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-coral transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Lembra
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
    <PageMeta
      title="Become a Lembra partner vendor | Apply to join"
      description="Apply to join Lembra's network of independent Melbourne vendors (bakeries, florists, hampers, spas, experiences). Reliable B2B demand, weekly payment terms, no cold outreach. Tell us about your business and what you make."
      path="/partner-apply"
    />
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="min-h-screen"
      style={{ background: "linear-gradient(165deg, #FFF8E7 0%, #FBF3D0 40%, #F7E8A4 70%, #FBF3D0 100%)" }}
    >
      {/* Header */}
      <header className="w-full" style={{ borderBottom: "1px solid rgba(0,0,0,0.06)", background: "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)" }}>
        <div className="container mx-auto flex items-center justify-between px-6 py-4">
          <Logo size="sm" />
          <Link to="/" className="flex items-center gap-1 text-sm font-medium text-navy hover:text-navy/70">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
      </header>

      {/* Progress dots */}
      <div className="flex justify-center gap-2 pt-8 pb-4">
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === step ? "w-8 bg-coral" : i < step ? "w-2 bg-coral/40" : "w-2 bg-charcoal/15"
            }`}
          />
        ))}
      </div>

      {/* Content */}
      <div className={`container mx-auto px-6 py-8 max-w-2xl transition-all duration-200 ${transitioning ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>

        {/* ── Step 0: About Your Business ── */}
        {step === 0 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">Tell us about your business</h2>
              <p className="text-charcoal/60">The basics first. We want to know who we're talking to.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
              {/* MS163: maxLength values match the zod schema max on each
                  field so the browser stops accepting input at the cap
                  instead of letting users type past it and hit a zod
                  reject on next() / submit. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RhfField label="Business name" name="bakery_name" placeholder="e.g., Sweet Tooth Cakery / Bloom Florists" form={form} required maxLength={200} />
                <RhfField label="Your name" name="contact_name" placeholder="First and last name" form={form} required maxLength={100} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RhfField label="Email address" name="contact_email" type="email" placeholder="hello@yourbusiness.com.au" form={form} required maxLength={320} />
                <RhfField label="Phone number" name="contact_phone" type="tel" placeholder="04XX XXX XXX" form={form} maxLength={30} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RhfField label="ABN" name="abn" placeholder="XX XXX XXX XXX" form={form} required hint="11-digit Australian Business Number" maxLength={30} />
                <RhfSelect
                  label="Years in business" name="years_in_business" form={form} required
                  options={[
                    { value: "less_than_1", label: "Less than 1 year" },
                    { value: "1_3", label: "1–3 years" },
                    { value: "3_5", label: "3–5 years" },
                    { value: "5_plus", label: "5+ years" },
                  ]}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RhfField label="Website" name="website" placeholder="www.yourbusiness.com.au" form={form} maxLength={200} />
                <RhfField label="Instagram" name="instagram" placeholder="@yourbusiness" form={form} maxLength={100} />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: What You Make ── */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">What do you make?</h2>
              <p className="text-charcoal/60">Select everything that applies. We love variety.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <p className="text-sm font-semibold text-navy mb-3">Products & treats <span className="text-coral">*</span></p>
                <div className="flex flex-wrap gap-2">
                  {productOptions.map((p) => (
                    <CheckChip
                      key={p} label={p}
                      checked={data.product_types.includes(p)}
                      onToggle={() => form.setValue("product_types", toggle(data.product_types, p), { shouldValidate: true })}
                    />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-navy mb-3">Dietary accommodations <span className="text-coral">*</span></p>
                <p className="text-xs text-charcoal/50 mb-3">Lembra teams have real dietary needs. This matters a lot to us.</p>
                <div className="flex flex-wrap gap-2">
                  {dietaryOpts.map((d) => (
                    <CheckChip
                      key={d} label={d}
                      checked={data.dietary_options.includes(d)}
                      onToggle={() => form.setValue("dietary_options", toggle(data.dietary_options, d), { shouldValidate: true })}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Your Craft ── */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">Tell us about your craft</h2>
              <p className="text-charcoal/60">We want to understand your style and what makes you special.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <p className="text-sm font-semibold text-navy mb-3">Customisation level <span className="text-coral">*</span></p>
                <div className="grid gap-3">
                  {[
                    { v: "standard", title: "Standard", desc: "Set menu with fixed designs, no customisation required" },
                    { v: "moderate", title: "Moderate", desc: "Custom text, colours, and minor design adjustments" },
                    { v: "full", title: "Full bespoke", desc: "Completely custom designs, sketches, and one-of-a-kind creations" },
                  ].map((opt) => (
                    <OptionCard
                      key={opt.v} title={opt.title} desc={opt.desc}
                      selected={data.customisation_level === opt.v}
                      onClick={() => form.setValue("customisation_level", opt.v, { shouldValidate: true })}
                    />
                  ))}
                </div>
              </div>
              <RhfTextarea
                label="Your signature style" name="signature_style" form={form}
                placeholder="Describe your most popular item or what defines your baking. What makes customers come back?"
                minChars={50} required
              />
              <RhfSelect
                label="Typical price per celebration order" name="price_range" form={form} required
                options={[
                  { value: "under_100", label: "Under $100" },
                  { value: "100_200", label: "$100–$200" },
                  { value: "200_400", label: "$200–$400" },
                  { value: "400_plus", label: "$400+" },
                  { value: "variable", label: "Variable, quoted per order" },
                ]}
              />
            </div>
          </div>
        )}

        {/* ── Step 3: Delivery & Logistics ── */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">Delivery & logistics</h2>
              <p className="text-charcoal/60">Reliable delivery is the last piece of the celebration puzzle.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <p className="text-sm font-semibold text-navy mb-3">How do you handle delivery? <span className="text-coral">*</span></p>
                <div className="grid gap-3">
                  {[
                    { v: "self", title: "We deliver ourselves", desc: "Our own vehicles and drivers" },
                    { v: "courier", title: "We use a courier service", desc: "DoorDash, GoGet, or a specialist courier" },
                    { v: "both", title: "Both, depends on the order", desc: "We're flexible" },
                  ].map((opt) => (
                    <OptionCard
                      key={opt.v} title={opt.title} desc={opt.desc}
                      selected={data.delivery_type === opt.v}
                      onClick={() => form.setValue("delivery_type", opt.v, { shouldValidate: true })}
                    />
                  ))}
                </div>
              </div>
              <RhfTextarea
                label="Suburbs & areas you deliver to" name="delivery_suburbs" form={form}
                placeholder="e.g., All Melbourne CBD and inner suburbs (3000–3207), South Yarra, Fitzroy, Collingwood..."
                required
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <RhfSelect
                  label="Minimum notice required" name="min_notice_days" form={form} required
                  parseAs="number"
                  options={[
                    { value: "0", label: "Same day (we love a challenge!)" },
                    { value: "1", label: "24 hours" },
                    { value: "2", label: "48 hours" },
                    { value: "3", label: "3+ days" },
                    { value: "7", label: "1 week+" },
                  ]}
                />
                <RhfSelect
                  label="Max orders per week" name="max_orders_per_week" form={form} required
                  options={[
                    { value: "1_5", label: "1–5 orders" },
                    { value: "5_15", label: "5–15 orders" },
                    { value: "15_30", label: "15–30 orders" },
                    { value: "30_plus", label: "30+ orders" },
                  ]}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── Step 4: Capacity & Fit ── */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">Capacity & fit</h2>
              <p className="text-charcoal/60">Our teams range from 2 to 50+ people. Help us understand who you're best suited for.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <div>
                <p className="text-sm font-semibold text-navy mb-3">Team sizes you can comfortably serve <span className="text-coral">*</span></p>
                <div className="grid gap-3">
                  {teamSizeOpts.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => form.setValue("team_sizes_served", toggle(data.team_sizes_served, opt.value), { shouldValidate: true })}
                      className={`flex items-center gap-4 rounded-2xl p-4 border-2 transition-all ${
                        data.team_sizes_served.includes(opt.value)
                          ? "border-coral bg-white shadow-md"
                          : "border-transparent bg-white/70 hover:border-coral/30 hover:shadow-sm"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${
                        data.team_sizes_served.includes(opt.value) ? "border-coral bg-coral" : "border-charcoal/30"
                      }`}>
                        {data.team_sizes_served.includes(opt.value) && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span className="text-navy font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-navy mb-3">Urgent orders (less than 24 hrs notice) <span className="text-coral">*</span></p>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { v: "yes", label: "Yes, no problem" },
                    { v: "sometimes", label: "Sometimes" },
                    { v: "no", label: "No, need notice" },
                  ].map((opt) => (
                    <OptionCard
                      key={opt.v} title={opt.label}
                      selected={data.handles_urgent_orders === opt.v}
                      onClick={() => form.setValue("handles_urgent_orders", opt.v, { shouldValidate: true })}
                    />
                  ))}
                </div>
              </div>
              <RhfField
                label="Existing corporate clients" name="existing_corporate_clients" form={form}
                placeholder="Any businesses or offices you currently bake for? (optional)"
              />
            </div>
          </div>
        )}

        {/* ── Step 5: The Pitch ── */}
        {step === 5 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">About you</h2>
              <p className="text-charcoal/60">What should we know before we read your application?</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
              <RhfTextarea
                label="Why do you want to partner with Lembra?" name="why_lembra" form={form}
                placeholder="Tell us what excites you about this opportunity, what you'd bring to Lembra's clients, and why you think it's a great fit for your business..."
                minChars={150} required
              />
              <RhfSelect
                label="How did you hear about Lembra?" name="referral_source" form={form} required
                options={[
                  { value: "instagram", label: "Instagram" },
                  { value: "google", label: "Google search" },
                  { value: "referral", label: "Referral from another vendor" },
                  { value: "word_of_mouth", label: "Word of mouth" },
                  { value: "linkedin", label: "LinkedIn" },
                  { value: "event", label: "Industry event" },
                  { value: "other", label: "Other" },
                ]}
              />
              <RhfTextarea
                label="Anything else we should know?" name="additional_notes" form={form}
                placeholder="Awards, press features, notable clients, anything that makes you stand out... (optional)"
              />
            </div>
          </div>
        )}

        {/* ── Step 6: Review & Submit ── */}
        {step === 6 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">Review your application</h2>
              <p className="text-charcoal/60">Everything look right? Hit submit when you're ready.</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h3 className="font-bold text-navy mb-3">About your business</h3>
              <SummaryRow label="Business name" value={data.bakery_name} />
              <SummaryRow label="Contact" value={`${data.contact_name} · ${data.contact_email}`} />
              <SummaryRow label="ABN" value={data.abn} />
              <SummaryRow label="Years in business" value={data.years_in_business} />
              {data.instagram && <SummaryRow label="Instagram" value={data.instagram} />}

              <h3 className="font-bold text-navy mt-5 mb-3">Products & craft</h3>
              <SummaryRow label="Products" value={data.product_types} />
              <SummaryRow label="Dietary options" value={data.dietary_options} />
              <SummaryRow label="Customisation" value={data.customisation_level} />
              <SummaryRow label="Price range" value={data.price_range} />

              <h3 className="font-bold text-navy mt-5 mb-3">Delivery & capacity</h3>
              <SummaryRow label="Delivery" value={data.delivery_type} />
              <SummaryRow label="Suburbs" value={data.delivery_suburbs} />
              <SummaryRow label="Min notice" value={`${data.min_notice_days} day(s)`} />
              <SummaryRow label="Max orders/wk" value={data.max_orders_per_week} />
              <SummaryRow label="Team sizes" value={data.team_sizes_served} />
              <SummaryRow label="Urgent orders" value={data.handles_urgent_orders} />
            </div>

            <div className="bg-butter/60 rounded-2xl p-5 border-2 border-butter">
              <p className="text-sm text-navy/80">
                <strong>What happens next:</strong> Our team reviews every application personally. We'll be in touch at <strong>{data.contact_email}</strong> within 3 business days. If approved, you'll receive instructions to create your vendor partner account.
              </p>
            </div>
          </div>
        )}

        {/* ── Error display (only for current step) ── */}
        {stepErrors.length > 0 && (
          <div className="mt-4 bg-coral/10 border-2 border-coral/30 rounded-xl p-4 space-y-1">
            {stepErrors.map((e) => (
              <p key={e} className="text-sm text-coral font-medium flex items-center gap-1.5">
                <span>•</span> {e}
              </p>
            ))}
          </div>
        )}

        {/* ── Navigation ── */}
        <div className="flex gap-3 mt-8 pb-12">
          {step > 0 && (
            <Button type="button" variant="outline" onClick={goBack} className="rounded-full px-6">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          )}
          {step < 6 ? (
            <Button type="button" variant="primary" onClick={handleNext} className="rounded-full px-8 flex-1 sm:flex-initial">
              Continue
            </Button>
          ) : (
            <Button
              type="submit"
              variant="primary"
              disabled={submitting}
              className="rounded-full px-8 flex-1 sm:flex-initial"
            >
              {submitting ? "Submitting…" : "Submit application"}
            </Button>
          )}
        </div>
      </div>
    </form>
    </>
  );
}

// ─── rhf-bound field helpers ──────────────────────────────────
// These keep the existing visual styling (native <input>, <textarea>,
// <select> with Tailwind classes) so the page looks identical after the
// migration. Each helper binds to a specific form field by name and
// surfaces the first zod error inline under the input.

type FormRef = ReturnType<typeof useForm<AppData>>;

function RhfField({
  label, name, placeholder, type = "text", required = false, hint = "", form, maxLength,
}: {
  label: string; name: keyof AppData; placeholder?: string; type?: string;
  required?: boolean; hint?: string; form: FormRef;
  // MS163: client-side maxLength so users stop typing at the cap
  // instead of seeing a zod error post-submit. Each caller passes
  // the matching schema max value.
  maxLength?: number;
}) {
  const err = form.formState.errors[name]?.message as string | undefined;
  const registered = form.register(name as Path<AppData>);
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-1.5">
        {label}{required && <span className="text-coral ml-0.5">*</span>}
      </label>
      {hint && <p className="text-xs text-charcoal/50 mb-1.5">{hint}</p>}
      <input
        type={type}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full rounded-xl border-2 border-charcoal/15 bg-white px-4 py-3 text-navy placeholder:text-charcoal/40 focus:border-coral focus:outline-none transition-colors"
        {...registered}
      />
      {err && <p className="mt-1 text-xs text-coral font-medium">{err}</p>}
    </div>
  );
}

function RhfSelect({
  label, name, options, required = false, parseAs, form,
}: {
  label: string; name: keyof AppData; options: { value: string; label: string }[];
  required?: boolean; parseAs?: "number"; form: FormRef;
}) {
  const err = form.formState.errors[name]?.message as string | undefined;
  const value = form.watch(name);
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-1.5">
        {label}{required && <span className="text-coral ml-0.5">*</span>}
      </label>
      <div className="relative">
        <select
          value={parseAs === "number" ? String(value ?? "") : (value as string) || ""}
          onChange={(e) => {
            const raw = e.target.value;
            const next = parseAs === "number" ? parseInt(raw, 10) : raw;
            form.setValue(name, next as AppData[typeof name], { shouldValidate: true });
          }}
          className="w-full appearance-none rounded-xl border-2 border-charcoal/15 bg-white px-4 py-3 text-navy focus:border-coral focus:outline-none transition-colors"
        >
          <option value="">Select…</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-charcoal/50" />
      </div>
      {err && <p className="mt-1 text-xs text-coral font-medium">{err}</p>}
    </div>
  );
}

function RhfTextarea({
  label, name, placeholder, minChars = 0, required = false, form,
}: {
  label: string; name: keyof AppData; placeholder?: string;
  minChars?: number; required?: boolean; form: FormRef;
}) {
  const err = form.formState.errors[name]?.message as string | undefined;
  const value = (form.watch(name) as string) || "";
  const registered = form.register(name as Path<AppData>);
  return (
    <div>
      <label className="block text-sm font-semibold text-navy mb-1.5">
        {label}{required && <span className="text-coral ml-0.5">*</span>}
      </label>
      <textarea
        placeholder={placeholder}
        rows={4}
        className="w-full rounded-xl border-2 border-charcoal/15 bg-white px-4 py-3 text-navy placeholder:text-charcoal/40 focus:border-coral focus:outline-none transition-colors resize-none"
        {...registered}
      />
      {/* MS409: was inverted — coral on success, grey on unmet. Coral is
          the error colour on this design system; flip it so coral warns
          the user they need more characters, charcoal/40 quietly
          confirms they've met the minimum. */}
      {minChars > 0 && (
        <p className={`text-xs mt-1 ${value.length >= minChars ? "text-charcoal/40" : "text-coral"}`}>
          {value.length}/{minChars} characters minimum
        </p>
      )}
      {err && <p className="mt-1 text-xs text-coral font-medium">{err}</p>}
    </div>
  );
}

// Flatten rhf's nested error object into a list of messages for the given
// field names. Used to render the coral error box at the bottom of the
// current step.
function collectStepErrors(
  errors: Record<string, unknown>,
  fields: (keyof AppData)[],
): string[] {
  const out: string[] = [];
  for (const f of fields) {
    const entry = errors[f as string] as { message?: unknown } | undefined;
    const msg = entry?.message;
    if (typeof msg === "string") out.push(msg);
  }
  return out;
}
